import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { ProtectedRoute } from "@/lib/protected-route";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import RequestNew from "@/pages/RequestNew";
import ProjectDetail from "@/pages/ProjectDetail";
import Points from "@/pages/Points";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminProjects from "@/pages/AdminProjects";
import AdminProjectDetail from "@/pages/AdminProjectDetail";
import AdminClients from "@/pages/AdminClients";
import AdminPayments from "@/pages/AdminPayments";
import AdminDesigners from "@/pages/AdminDesigners";
import AdminPortfolio from "@/pages/AdminPortfolio";
import AdminCharges from "@/pages/AdminCharges";
import AdminEmailDiagnostics from "@/pages/AdminEmailDiagnostics";
import AdminMaintenanceBroadcast from "@/pages/AdminMaintenanceBroadcast";
import AdminReviews from "@/pages/AdminReviews";
import NewReview from "@/pages/NewReview";
import DesignerDashboard from "@/pages/DesignerDashboard";
import DesignerProjectDetail from "@/pages/DesignerProjectDetail";
import PortfolioDetail from "@/pages/PortfolioDetail";
import Portfolio from "@/pages/Portfolio";
import Account from "@/pages/Account";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CustomCharge from "@/pages/CustomCharge";
import Press from "@/pages/Press";
import AdminArchive from "@/pages/AdminArchive";
import AdminDigest from "@/pages/AdminDigest";
import AdminPromoCodes from "@/pages/AdminPromoCodes";
import AdminInvoices from "@/pages/AdminInvoices";
import AdminReceipts from "@/pages/AdminReceipts";
import AdminMeetings from "@/pages/AdminMeetings";
import MeetingRequest from "@/pages/MeetingRequest";
import ClientInvoice from "@/pages/ClientInvoice";
import PublicInvoicePay from "@/pages/PublicInvoicePay";

function ClientHomeRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === null) return null;
  // Anyone (guest, client, designer, admin) can view the public landing.
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            theme="dark"
            position="top-right"
            toastOptions={{
              style: { background: "#121212", border: "1px solid #262626", color: "#fafafa" },
            }}
          />
          <Routes>
            <Route path="/" element={<ClientHomeRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/work/:id" element={<PortfolioDetail />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/press" element={<Press />}/>
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/pay/invoice/:token" element={<PublicInvoicePay />} />
            <Route path="/meet" element={<MeetingRequest />} />

            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/request/new" element={<ProtectedRoute><RequestNew /></ProtectedRoute>} />
            <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
            <Route path="/points" element={<ProtectedRoute><Points /></ProtectedRoute>} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute anyRole><Account /></ProtectedRoute>} />
            <Route path="/charges/:id" element={<ProtectedRoute><CustomCharge /></ProtectedRoute>} />
            <Route path="/invoices/:id" element={<ProtectedRoute anyRole><ClientInvoice /></ProtectedRoute>} />

            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/projects" element={<ProtectedRoute adminOnly><AdminProjects /></ProtectedRoute>} />
            <Route path="/admin/archive" element={<ProtectedRoute adminOnly><AdminArchive /></ProtectedRoute>} />
            <Route path="/admin/digest" element={<ProtectedRoute adminOnly><AdminDigest /></ProtectedRoute>} />
            <Route path="/admin/promo-codes" element={<ProtectedRoute adminOnly><AdminPromoCodes /></ProtectedRoute>} />
            <Route path="/admin/invoices" element={<ProtectedRoute adminOnly><AdminInvoices /></ProtectedRoute>} />
            <Route path="/admin/receipts" element={<ProtectedRoute adminOnly><AdminReceipts /></ProtectedRoute>} />
            <Route path="/admin/meetings" element={<ProtectedRoute adminOnly><AdminMeetings /></ProtectedRoute>} />
            <Route path="/admin/projects/:id" element={<ProtectedRoute adminOnly><AdminProjectDetail /></ProtectedRoute>} />
            <Route path="/admin/clients" element={<ProtectedRoute adminOnly><AdminClients /></ProtectedRoute>} />
            <Route path="/admin/designers" element={<ProtectedRoute adminOnly><AdminDesigners /></ProtectedRoute>} />
            <Route path="/admin/payments" element={<ProtectedRoute adminOnly><AdminPayments /></ProtectedRoute>} />
            <Route path="/admin/portfolio" element={<ProtectedRoute adminOnly><AdminPortfolio /></ProtectedRoute>} />
            <Route path="/admin/charges" element={<ProtectedRoute adminOnly><AdminCharges /></ProtectedRoute>} />
            <Route path="/admin/email" element={<ProtectedRoute adminOnly><AdminEmailDiagnostics /></ProtectedRoute>} />
            <Route path="/admin/maintenance/broadcast" element={<ProtectedRoute adminOnly><AdminMaintenanceBroadcast /></ProtectedRoute>} />
            <Route path="/admin/reviews" element={<ProtectedRoute adminOnly><AdminReviews /></ProtectedRoute>} />
            <Route path="/reviews/new" element={<ProtectedRoute><NewReview /></ProtectedRoute>} />

            <Route path="/designer" element={<ProtectedRoute designerOnly><DesignerDashboard /></ProtectedRoute>} />
            <Route path="/designer/projects" element={<ProtectedRoute designerOnly><DesignerDashboard /></ProtectedRoute>} />
            <Route path="/designer/projects/:id" element={<ProtectedRoute designerOnly><DesignerProjectDetail /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
